import { useEffect, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { useLeftSidebarState } from '../left-sidebar-controller/index.tsx';
import { useRightSidebarState } from '../right-sidebar-controller/index.tsx';
import type { PreviewPanelState, RightSidebarState } from '../../../../common/types.tsx';
import { usePreviewPanelState } from '../preview-panel/index.tsx';
import { setRemoteLayoutState } from './set-remote-layout-state/index.tsx';

export const LayoutRemoteStateUpdater = () => {
	const accountId = useAccountId();
	const leftSidebar = useLeftSidebarState();
	const rightSidebar = useRightSidebarState();
	const previewPanel = usePreviewPanelState();

	const isLeftSidebarCollapsed = leftSidebar.isCollapsed;
	const leftSidebarWidth = leftSidebar.width;

	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();
	const [localPreviewPanelState, setLocalPreviewPanelState] = useState<PreviewPanelState>();

	// compares local vs external state coming via chain of callbacks
	// to determine if we really need to send an update of layout state
	const sideBarChanged = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
			externalPreviewPanelState: PreviewPanelState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			if (!isEqual(externalPreviewPanelState, localPreviewPanelState)) {
				setLocalPreviewPanelState(externalPreviewPanelState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[
			localLeftSidebarCollapsedState,
			localLeftSidebarWidthState,
			localRightSidebarState,
			localPreviewPanelState,
		],
	);

	useEffect(() => {
		if (!accountId) {
			return;
		}
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(leftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebar);
			setLocalPreviewPanelState(previewPanel);
			return;
		}
		if (!sideBarChanged(leftSidebarWidth, isLeftSidebarCollapsed, rightSidebar, previewPanel))
			return;

		setRemoteLayoutState({
			accountId,
			state: {
				rightSidebar,
				leftSidebar: {
					isCollapsed: isLeftSidebarCollapsed,
					width: leftSidebarWidth,
				},
				...(fg('jira-glance-panel') && { previewPanel }),
			},
		});
	}, [
		accountId,
		isLeftSidebarCollapsed,
		leftSidebarWidth,
		rightSidebar,
		initialLoad,
		previewPanel,
		sideBarChanged,
	]);

	return null;
};
